import { Button, InputGroup, Intent, Spinner, Tag } from '@blueprintjs/core'
import * as React from 'react'
import Client from '../../../client'
import CategoryColumn from './CategoryColumn'
import { CategorySearchRow } from './CategorySearchRow'
import {
  CategoryListContainerStyle,
  CategoryListStyle,
  CategoryMultiselectBodyStyle,
  CategoryMultiselectContainerStyle,
  CategoryMultiselectFooterStyle,
  CategoryMultiselectLoadingOverlayStyle,
  CategoryMultiselectStyle,
  CategorySearchOverlayHeadingStyle,
  CategorySearchOverlayStyle,
  CategorySearchStyle
} from './styles'
import { CategoryMultiselectCategory } from './types'

export interface CategoryMultiselectProps {
  onSelectionChange?: (selection: number[]) => void
  selection?: number[]
  height?: number
}

export interface CategoryMultiselectState {
  categories: CategoryMultiselectCategory[]
  highlightedPath?: number[]
  checkedSet: Set<number>
  selectedCategoriesString: string
  selectAll: boolean
  query: string
  loading: boolean
  flatCategoryCache: CategoryMultiselectCategory[]
}

export class CategoryMultiselect extends React.Component<CategoryMultiselectProps, CategoryMultiselectState> {
  constructor(props: CategoryMultiselectProps) {
    super(props)
    this.state = {
      categories: [],
      checkedSet: new Set(props.selection),
      highlightedPath: [],
      flatCategoryCache: [],
      query: '',
      selectedCategoriesString: '',
      loading: false,
      selectAll: false
    }
  }

  static generateSelectedCategoriesString(
    flatCategoryCache: CategoryMultiselectCategory[],
    checkedSet: Set<number>
  ): string {
    if (checkedSet.size === 0) return ''
    if (flatCategoryCache.length === checkedSet.size) return 'All Categories'

    return flatCategoryCache
      .filter((c: CategoryMultiselectCategory) => checkedSet.has(c.id))
      .map((c: CategoryMultiselectCategory) => c.name)
      .join(', ')
  }

  fetchCategories = async (): Promise<void> => {
    this.setState({ loading: true })
    try {
      const res = await Client.categoryV2Index('all')
      const categories = res.data.data
      const flatCategoryCache = categories.reduce(
        (acc: CategoryMultiselectCategory[], c: CategoryMultiselectCategory) => [
          ...acc,
          c,
          ...CategoryMultiselect.findCategoryDescendents(c)
        ],
        [] as CategoryMultiselectCategory[]
      )
      this.setState(s => ({
        categories,
        flatCategoryCache,
        loading: false,
        selectAll: flatCategoryCache.length === s.checkedSet.size,
        selectedCategoriesString: CategoryMultiselect.generateSelectedCategoriesString(flatCategoryCache, s.checkedSet)
      }))
    } catch (_error) {
      this.setState({ loading: false })
    }
  }

  static findCategoryDescendents(category: CategoryMultiselectCategory): CategoryMultiselectCategory[] {
    const concatWithChildren = (current: CategoryMultiselectCategory): CategoryMultiselectCategory[] => {
      if (category.children.length === 0) return [current]
      return [
        current,
        ...current.children.reduce<CategoryMultiselectCategory[]>((acc, c) => acc.concat(concatWithChildren(c)), [])
      ]
    }
    return category.children.reduce<CategoryMultiselectCategory[]>((acc, c) => acc.concat(concatWithChildren(c)), [])
  }

  onCheck = (category: CategoryMultiselectCategory): void => {
    const { onSelectionChange } = this.props
    const { checkedSet } = this.state
    const affectedCategories = [
      category.id,
      ...CategoryMultiselect.findCategoryDescendents(category).map((c: CategoryMultiselectCategory) => c.id)
    ]

    if (checkedSet.has(category.id)) {
      affectedCategories.forEach(d => checkedSet.delete(d))
      this.setState(
        s => ({
          checkedSet,
          selectAll: false,
          selectedCategoriesString: CategoryMultiselect.generateSelectedCategoriesString(
            s.flatCategoryCache,
            s.checkedSet
          )
        }),
        () => onSelectionChange && onSelectionChange(Array.from(checkedSet))
      )
    } else {
      affectedCategories.forEach(d => checkedSet.add(d))
      this.setState(
        s => ({
          checkedSet,
          selectedCategoriesString: CategoryMultiselect.generateSelectedCategoriesString(
            s.flatCategoryCache,
            s.checkedSet
          )
        }),
        () => onSelectionChange && onSelectionChange(Array.from(checkedSet))
      )
    }
  }

  componentDidUpdate(prevProps: CategoryMultiselectProps): void {
    if (prevProps.selection !== this.props.selection) {
      this.setState({ checkedSet: new Set(this.props.selection) })
    }
  }

  onCheckAll = (): void => {
    const { onSelectionChange } = this.props
    const { selectAll } = this.state
    const categoryIds = !selectAll ? this.state.flatCategoryCache.map(c => c.id) : []

    this.setState(
      s => ({
        checkedSet: new Set(categoryIds),
        selectAll: !s.selectAll,
        selectedCategoriesString: !s.selectAll ? 'All Categories' : ''
      }),
      () => onSelectionChange && onSelectionChange(categoryIds)
    )
  }

  onHighlight = (highlightedPath: number[]): void => {
    this.setState({ highlightedPath })
  }

  componentDidMount(): void {
    this.fetchCategories()
  }

  render(): React.ReactNode {
    const { loading, categories, flatCategoryCache, checkedSet, query, selectAll, selectedCategoriesString } =
      this.state
    // Ensure highlightedPath is always an array
    const highlightedPath: number[] = this.state.highlightedPath || []
    const height = this.props.height || 300
    return (
      <div style={CategoryMultiselectContainerStyle}>
        <div style={CategorySearchStyle}>
          <InputGroup
            leftIcon="search"
            value={query}
            placeholder="Search"
            rightElement={
              <Tag intent={Intent.NONE} minimal>
                {checkedSet.size} categor{checkedSet.size === 1 ? 'y' : 'ies'} selected
              </Tag>
            }
            disabled={loading}
            className="category-multiselect-search bp3-fill"
            onChange={e => this.setState({ query: e.target.value })}
          />
        </div>
        <div style={{ ...CategoryMultiselectBodyStyle, height: Math.max(height - 100, 50) }}>
          {loading && (
            <div style={CategoryMultiselectLoadingOverlayStyle}>
              <Spinner />
            </div>
          )}
          {!loading && query.length > 0 && (
            <div style={CategorySearchOverlayStyle}>
              <h5 style={CategorySearchOverlayHeadingStyle}>
                Search Results
                <Button
                  small
                  minimal
                  text="Close"
                  intent="primary"
                  onClick={() => this.setState({ query: '' })}
                  style={{ marginLeft: 5, position: 'relative', top: -1.5 }}
                />
              </h5>
              {flatCategoryCache
                .map(c => {
                  const matchIndex = c.pathCache.toLowerCase().indexOf(query.toLowerCase())

                  return matchIndex > -1 ? (
                    <CategorySearchRow
                      category={c}
                      key={`category-search-${c.id}`}
                      onCheck={this.onCheck}
                      checked={checkedSet.has(c.id)}
                      query={query}
                      matchIndex={matchIndex}
                    />
                  ) : null
                })
                .filter(c => c)}
            </div>
          )}
          {!loading && (
            <div style={CategoryMultiselectStyle}>
              <CategoryColumn
                categories={categories}
                onCheck={this.onCheck}
                onHighlight={this.onHighlight}
                checkedSet={checkedSet}
                highlightedPath={highlightedPath}
              />
            </div>
          )}
        </div>
        <div style={CategoryMultiselectFooterStyle}>
          <Button
            minimal
            intent="primary"
            disabled={loading}
            text={selectAll ? 'Clear selection' : 'Select all'}
            onClick={this.onCheckAll}
          />
        </div>
        <div style={CategoryListContainerStyle}>
          {!loading && (
            <p style={CategoryListStyle}>
              {checkedSet.size > 0 ? (
                <>
                  <b>Selection: </b>
                  {selectedCategoriesString}
                </>
              ) : (
                'No Selection'
              )}
            </p>
          )}
        </div>
      </div>
    )
  }
}
